<template>
  <main class="bg-gray-200 min-h-screen">
    <header>
      <div class="border-b border-gray-300">
        <div
          class="max-w-2xl lg:border-r border-gray-300 px-4 py-4 sm:flex sm:items-center sm:justify-between sm:px-6 lg:px-8"
        >
          <div class="flex-1 min-w-0">
            <h1 class="text-lg font-medium leading-6 text-gray-900 sm:truncate">
              {{ $t('invoices_done_title') }}
            </h1>
          </div>
          <div class="mt-4 flex space-x-2 sm:mt-0 sm:ml-4">
            <sqr-button
              class="order-3 sm:order-1 hidden md:block"
              :label="$t('invoices_index')"
              color="transparent"
              @click="index()"
              :loading="indexing"
            />
            <span class="order-2 sm:order-2 shadow-sm rounded-md ">
              <router-link :to="{ name: 'invoices-export' }">
                <button
                  type="button"
                  class="inline-flex items-center px-4 py-2 border border-gray-300 text-sm leading-5 font-medium rounded-md text-gray-700 bg-white hover:text-gray-500 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 active:text-gray-800 active:bg-gray-50 transition duration-150 ease-in-out"
                >
                  {{ $t('invoices_export') }}
                </button>
              </router-link>
            </span>
            <span class="order-1 sm:order-3 shadow-sm rounded-md">
              <router-link :to="{ name: 'invoice-add' }">
                <button
                  type="button"
                  class="inline-flex items-center px-4 py-2 border border-transparent text-sm leading-5 font-medium rounded-md text-white bg-green-600 hover:bg-green-500 focus:outline-none focus:shadow-outline-blue focus:border-green-700 active:bg-green-700 transition duration-150 ease-in-out"
                >
                  {{ $t('invoices_add') }}
                </button>
              </router-link>
            </span>
          </div>
        </div>
      </div>
      <sqr-progress v-show="loading" />
      <sqr-error-banner :error="loadError" @retry="load({ reload: true })" />
      <sqr-error-banner :error="indexError" />
    </header>

    <div class="px-6 lg:px-4">
      <div class="flex space-x-4 py-6 justify-start">
        <sqr-input-select
          :label="$t('invoices_year')"
          v-model="year"
          :options="yearOptions"
          @change="reload()"
        />
        <sqr-input-select
          :label="$t('invoices_sort_by')"
          v-model="sort"
          :options="sortOptions"
          @change="reload()"
        />
      </div>
    </div>

    <div v-show="invoices.length > 0" class="md:px-4 lg:px-4 max-w-2xl">
      <div class="bg-white shadow  md:rounded-md">
        <ul>
          <li
            v-for="(invoice, index) in invoices"
            :key="invoice.id"
            :class="{ 'border-t border-gray-200': index > 0 }"
          >
            <router-link
              :to="{ name: 'invoice-view', params: { aid, id: invoice.id } }"
              class="block hover:bg-gray-50 focus:outline-none focus:bg-gray-50 transition duration-150 ease-in-out"
            >
              <div class="px-4 py-4 sm:px-6">
                <div class="flex items-center justify-between">
                  <div
                    class="text-sm leading-5 font-normal text-blue-600 truncate"
                  >
                    <div class="mb-1">
                      <span class="font-bold">{{
                        invoice.patientFamilyName
                      }}</span
                      >,
                      {{ invoice.patientGivenName }}
                    </div>
                    <div class="sm:flex">
                      <div
                        class="mr-6 flex items-center text-sm leading-5 text-gray-600 font-light"
                      >
                        <CalendarIcon
                          class="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400"
                        />
                        {{ invoice.date | date }}
                      </div>
                      <div
                        class="mt-2 flex items-center text-sm leading-5 text-gray-600 font-light sm:mt-0"
                      >
                        <QrcodeIcon
                          class="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400"
                        />
                        {{ invoice.number | invoiceNumber }}
                      </div>
                    </div>
                  </div>
                  <div class="flex flex-between space-x-4">
                    <div
                      class="ml-2 flex-shrink-0 flex flex-col text-right"
                      v-show="invoice.deposit.amount"
                    >
                      <div class="font-medium text-gray-700">
                        {{ invoice.deposit | amount }}
                      </div>
                      <div class="text-xs font-medium text-gray-500">
                        {{ $t('invoice_deposit') }}
                      </div>
                    </div>

                    <div class="ml-2 flex-shrink-0 flex flex-col text-right">
                      <div class="font-medium">
                        {{ invoice.totalTi | amount }}
                      </div>
                      <div class="text-xs font-medium text-gray-500">
                        {{ $t('invoice_total_ti') }}
                      </div>
                    </div>

                    <!-- invoice menu -->
                    <div class="relative flex justify-end items-center">
                      <button
                        aria-has-popup="true"
                        type="button"
                        class="w-8 h-8 inline-flex items-center justify-center text-gray-500 rounded-full bg-transparent hover:text-gray-500 focus:outline-none focus:text-gray-500 focus:bg-gray-100 transition ease-in-out duration-150"
                        @click.prevent="
                          menuInvoice =
                            menuInvoice === invoice.id ? '' : invoice.id
                        "
                      >
                        <!-- Heroicon name: dots-vertical -->
                        <DotsVerticalIcon class="h-6 w-6 fill-current" />
                      </button>
                      <transition
                        enter-active-class="transition ease-out duration-100"
                        enter-class="transform opacity-0 scale-95"
                        enter-to-class="transform opacity-100 scale-100"
                        leave-active-class="transition ease-in duration-75"
                        leave-class="transform opacity-100 scale-100"
                        leave-to-class="transform opacity-0 scale-95"
                      >
                        <div
                          class="mx-3 origin-top-right absolute right-7 top-0 w-48 mt-1 rounded-md shadow-lg"
                          v-show="invoice.id === menuInvoice"
                        >
                          <div
                            class="z-10 rounded-md bg-white shadow-xs"
                            role="menu"
                            aria-orientation="vertical"
                            aria-labelledby="project-options-menu-0"
                          >
                            <div class="py-1">
                              <router-link
                                :to="{
                                  name: 'invoice-view',
                                  params: { aid, id: invoice.id }
                                }"
                                class="group flex items-center px-4 py-2 text-sm leading-5 text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100 focus:text-gray-900"
                                role="menuitem"
                              >
                                <PencilAltIcon
                                  class="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500 group-focus:text-gray-500"
                                />
                                {{ $t('invoice_view') }}
                              </router-link>
                              <router-link
                                :to="{
                                  name: 'invoice-clone',
                                  params: { aid, id: invoice.id }
                                }"
                                class="group flex items-center px-4 py-2 text-sm leading-5 text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100 focus:text-gray-900"
                                role="menuitem"
                              >
                                <DuplicateIcon
                                  class="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500 group-focus:text-gray-500"
                                />
                                {{ $t('invoice_clone') }}
                              </router-link>
                              <router-link
                                :to="{
                                  name: 'invoice-email',
                                  params: { aid, id: invoice.id }
                                }"
                                class="group flex items-center px-4 py-2 text-sm leading-5 text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100 focus:text-gray-900"
                                role="menuitem"
                              >
                                <MailIcon
                                  class="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500 group-focus:text-gray-500"
                                />
                                {{ $t('invoice_email') }}
                              </router-link>
                            </div>
                            <div class="border-t border-gray-100"></div>
                            <div class="py-1">
                              <a
                                class="group flex items-center px-4 py-2 text-sm leading-5 text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100 focus:text-gray-900"
                                role="menuitem"
                                @click.prevent="confirmRemove(invoice)"
                              >
                                <TrashIcon
                                  class="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500 group-focus:text-gray-500"
                                />
                                {{ $t('invoice_delete') }}
                              </a>
                              <sqr-error-text
                                :error="removeError[invoice.path]"
                              />
                            </div>
                          </div>
                        </div>
                      </transition>
                    </div>
                  </div>
                </div>
              </div>
            </router-link>
          </li>
        </ul>
      </div>
    </div>

    <section class="py-12" v-if="invoices.length === 0">
      <div class="md:p-4 max-w-2xl">
        <div class="grid grid-cols-2 gap-x-6 items-center">
          <div class="col-span-1"><Empty /></div>
          <div class="col-span-1 text-lg">
            {{ $t('invoices_done_empty', { year }) }}
          </div>
        </div>
      </div>
    </section>
  </main>
</template>

<script>
import { mapState, mapActions } from 'vuex';

import {
  groupBy,
  pick,
  fromPairs,
  sortWith,
  ascend,
  descend,
  prop
} from 'ramda';

import SqrButton from '@/components/SqrButton';
import SqrProgress from '@/components/SqrProgress';
import SqrErrorBanner from '@/components/SqrErrorBanner';
import SqrErrorText from '@/components/SqrErrorText';
import SqrInputSelect from '@/components/SqrInputSelect';
import Empty from '@/components/Empty';

import date from '@/filters/date';
import patientLabel from '@/filters/patient';
import practiceLabel from '@/filters/practice';
import amount from '@/filters/amount';
import {
  CalendarIcon,
  DotsVerticalIcon,
  DuplicateIcon,
  MailIcon,
  PencilAltIcon,
  QrcodeIcon,
  TrashIcon
} from '@vue-hero-icons/outline';

export default {
  name: 'InvoicesDone',
  components: {
    SqrButton,
    SqrProgress,
    SqrErrorBanner,
    SqrErrorText,
    SqrInputSelect,
    Empty,
    CalendarIcon,
    QrcodeIcon,
    DotsVerticalIcon,
    PencilAltIcon,
    DuplicateIcon,
    MailIcon,
    TrashIcon
  },
  filters: {
    practiceLabel,
    date,
    patientLabel,
    amount,
    invoiceNumber(value) {
      if (!value) return 'N/A';
      return value.split(' ')[0];
    }
  },
  props: {
    aid: String
  },
  computed: {
    ...mapState('invoicesIndex', ['doc', 'loading', 'loadError']),
    ...mapState('remover', ['removeError']),
    invoices() {
      const index = this.doc?.invoicesById ?? {};
      const invoices = Object.entries(index).map(([id, invoice]) => ({
        ...invoice,
        id
      }));
      let sort;
      switch (this.sort) {
        default:
        case 'number':
          sort = sortWith([descend(prop('number'))]);
          break;
        case 'date':
          sort = sortWith([descend(prop('date'))]);
          break;
        case 'patient':
          sort = sortWith([
            ascend(prop('patientFamilyName')),
            ascend(prop('patientGivenName'))
          ]);
          break;
      }
      return sort(invoices);
    },
    yearOptions() {
      return [
        // TODO we should load this information from the account and update it with re-index
        { id: 2025, label: '2025' },
        { id: 2024, label: '2024' },
        { id: 2023, label: '2023' },
        { id: 2022, label: '2022' },
        { id: 2021, label: '2021' },
        { id: 2020, label: '2020' },
        { id: 2019, label: '2019' }
      ];
    },
    sortOptions() {
      return [
        {
          id: 'number',
          label: this.$t('invoices_sort_by_number_desc')
        },
        {
          id: 'date',
          label: this.$t('invoices_sort_by_date_desc')
        },
        {
          id: 'patient',
          label: this.$t('invoices_sort_by_patient')
        }
      ];
    }
  },
  data() {
    return {
      adding: false,
      indexing: false,
      indexError: null,
      menuInvoice: null,
      sort: 'number',
      year: new Date().getFullYear()
    };
  },
  mounted() {
    this.reload();
  },
  methods: {
    ...mapActions('invoicesIndex', ['sub', 'unsub', 'pageSet']),
    ...mapActions('remover', ['remove']),
    reload() {
      const year = this.year;
      const path = `accounts/${this.aid}/indexes/invoices-${year}`;
      this.sub({ path });
      this.$a().logEvent('invoices-done', { year });
    },
    async confirmRemove(invoice) {
      const ok = confirm(this.$t('invoices_remove_confirm', invoice));
      if (ok) {
        this.remove({ path: `accounts/${this.aid}/invoices/${invoice.id}` });
      }
    },
    async index() {
      try {
        this.indexing = true;
        const aid = this.aid;

        const snaps = await this.$db()
          .collection(`accounts/${aid}/invoices`)
          .where('status', '==', 'valid')
          .get();

        const fields = ['date', 'number', 'deposit', 'totalTi', 'updated'];
        const pickFields = invoice => ({
          ...pick(fields, invoice),
          patientGivenName: invoice.patient?.givenName ?? '',
          patientFamilyName: invoice.patient?.familyName ?? ''
        });

        const invoices = snaps.docs.map(snap => ({
          ...snap.data(),
          id: snap.id
        }));
        const byYear = groupBy(({ date }) => date.substring(0, 4), invoices);
        const batch = this.$db().batch();
        Object.entries(byYear).forEach(([year, invoices]) => {
          const invoicesById = fromPairs(
            invoices.map(invoice => [invoice.id, pickFields(invoice)])
          );
          const index = { invoicesById, year, type: 'invoices' };
          const path = `accounts/${aid}/indexes/invoices-${year}`;
          const ref = this.$db().doc(path);
          batch.set(ref, index);
        });
        await batch.commit();
      } catch (error) {
        this.indexError = error;
      } finally {
        this.indexing = false;
      }
    }
  }
};
</script>
